import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Bot, Check, Shield, MessageSquare, User, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { GlassButton } from './ui/GlassButton';
import api from '../services/api';

interface Account {
    id: number | string;
    phone: string;
    status?: string;
    is_active?: boolean;
    user_id?: string | null;
}

interface AddBotModalProps {
    onClose: () => void;
    onSuccess: () => void;
    accountId: number | string | null;
    accounts?: Account[];
    onSelectAccount?: (id: number | string) => void;
}

export const AddBotModal: React.FC<AddBotModalProps> = ({ onClose, onSuccess, accountId, accounts = [], onSelectAccount }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [subscriptionRequired, setSubscriptionRequired] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);
    const [floodWait, setFloodWait] = useState<number | null>(null);

    // Countdown Effect
    React.useEffect(() => {
        if (floodWait === null) return;
        if (floodWait <= 0) {
            setFloodWait(null);
            setError(''); // Clear error when done
            return;
        }
        const timer = setTimeout(() => setFloodWait(prev => (prev ? prev - 1 : 0)), 1000);
        return () => clearTimeout(timer);
    }, [floodWait]);

    // Conta: usar a do contexto ou a selecionada no dropdown do modal
    const [selectedAccountInModal, setSelectedAccountInModal] = useState<number | string | null>(null);
    const effectiveAccountId = accountId ?? selectedAccountInModal;

    useEffect(() => {
        if (!accountId && accounts.length > 0 && !selectedAccountInModal) {
            setSelectedAccountInModal(accounts[0].id);
        }
    }, [accountId, accounts, selectedAccountInModal]);

    // Form Data
    const [name, setName] = useState(''); // Display Name
    const [username, setUsername] = useState('');
    const [manuallyEdited, setManuallyEdited] = useState(false);
    const [description, setDescription] = useState('');
    const [welcomeMsg, setWelcomeMsg] = useState('');
    const [supportText, setSupportText] = useState('');

    // Results
    const [botId, setBotId] = useState<string | null>(null);
    const [finalBotUsername, setFinalBotUsername] = useState(''); // @bot

    const addLog = (msg: string) => setLogs(prev => [...prev, msg]);

    // --- ACTIONS ---

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setName(val);
        if (!manuallyEdited) {
            // Clean string: remove accents, special chars, spaces -> underscore
            const clean = val.toLowerCase()
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove accents
                .replace(/[^a-z0-9]/g, '_') // Replace non-alphanumeric with _
                .replace(/_+/g, '_'); // Dedupe underscores (optional)

            if (clean) {
                setUsername(clean + '_bot');
            } else {
                setUsername('');
            }
        }
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setManuallyEdited(true);
        setUsername(e.target.value);
    };

    const handleStep1Create = async () => {
        if (!effectiveAccountId) return setError('Selecione uma conta abaixo.');
        if (name && username && !username.toLowerCase().endsWith('bot')) return setError('Username deve terminar em \"bot\".');

        setLoading(true);
        setError('');
        addLog('Etapa 1: Criando Bot...');

        try {
            const res = await api.post('/bots/create-interactive', {
                account_id: effectiveAccountId,
                name: name || '',
                username: username ? username.replace('@', '') : ''
            });

            // res.data.bot_token unused in variable but useful for debug/log if needed.
            // Removed state setter to fix linter
            // setBotToken(res.data.bot_token); 
            setBotId(res.data.bot.id);
            setFinalBotUsername(`@${username.replace('@', '')}`);
            addLog('Bot criado com sucesso! Token recebido.');
            setStep(2);
        } catch (e: any) {
            console.error(e);
            const status = e.response?.status;
            const data = e.response?.data;

            if (status === 403 && data?.code === 'SUBSCRIPTION_REQUIRED') {
                setSubscriptionRequired(true);
                setError(data?.message || 'Assine um plano para cadastrar bots.');
                addLog('Plano pago necessário para cadastrar novo bot.');
                return;
            }

            let msg = data?.error || data?.detail || data?.message || 'Erro ao criar bot.';

            if (status === 429) {
                msg = `⚠️ ${msg}`; // Highlight FloodWait
                addLog("Bloqueio Temporário (FloodWait) detectado.");

                const match = msg.match(/(\d+)s/);
                if (match) {
                    setFloodWait(parseInt(match[1]));
                }
            }

            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleStep2Config = async () => {
        if (!description) {
            setStep(3); // Skip if empty? Or enforce? Let's allow skip.
            return;
        }

        setLoading(true);
        setError('');
        addLog('Etapa 2: Configurando Descrição...');

        try {
            // Proxy via server.js or direct to Python tools if CORS allows? 
            // Server.js doesn't have proxy for set-description yet. 
            // We will update server.js or assume we added it. 
            // Wait, I need to add /api/bots/set-description to server.js proxy logic!
            // For now, let's assume I'll add it or call Python directly if CORS open.
            // Frontend usually talks to port 3001 (Node).

            await api.post('/bots/set-description', {
                account_id: effectiveAccountId,
                bot_username: finalBotUsername,
                description: description
            });

            addLog('Descrição atualizada no BotFather.');
            setStep(3);
        } catch (e: any) {
            console.error(e);
            setError('Falha ao definir descrição. (Mas o bot já foi criado).');
            // Allow proceed anyway?
            setTimeout(() => setStep(3), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleStep3Final = async () => {
        setLoading(true);
        // Save local configs (Welcome, Support) to DB via Node API patch
        // We need to know the BOT ID or use username.
        // The Create API returned bot_id? No, I need to ensure it returns bot_id or I search by name.
        // Let's rely on name update.

        try {
            const identifier = botId || finalBotUsername;
            await api.put(`/bots/${identifier}`, {
                welcome_message: welcomeMsg,
                support_text: supportText
            });

            onSuccess();
            onClose();
        } catch (e) {
            console.error(e);
            // If fail, just close.
            onSuccess();
            onClose();
        } finally {
            setLoading(false);
        }
    };

    // --- RENDER HELPERS ---

    const renderStepIndicator = () => (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', justifyContent: 'center', gap: '8px' }}>
            {[1, 2, 3].map(s => (
                <React.Fragment key={s}>
                    <div style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        background: step >= s ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                        color: step >= s ? '#fff' : 'rgba(255,255,255,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 600, fontSize: '14px', border: step === s ? '2px solid rgba(59,130,246,0.5)' : 'none'
                    }}>
                        {step > s ? <Check size={16} /> : s}
                    </div>
                    {s < 3 && <div style={{ width: '40px', height: '2px', background: step > s ? '#3b82f6' : 'rgba(255,255,255,0.1)' }} />}
                </React.Fragment>
            ))}
        </div>
    );

    return (
        <div className="modal-overlay" onClick={loading ? undefined : onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3><Bot size={20} className="highlight-icon" /> Criar Novo Bot</h3>
                    {!loading && <button className="close-btn" onClick={onClose}><X size={20} /></button>}
                </div>

                <div className="modal-body">
                    {renderStepIndicator()}

                    {/* STEP 1: CREATE */}
                    {step === 1 && (
                        <>
                            <div className="step-title">
                                <h4>Informações Básicas</h4>
                                <p>Identidade do seu bot no Telegram.</p>
                            </div>

                            {accounts.length === 0 ? (
                                <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                                    <p style={{ color: '#fca5a5', margin: 0, fontSize: '14px' }}>
                                        Você precisa de uma conta Telegram vinculada para criar um bot. Feche este modal e clique em <strong>Adicionar conta</strong> no menu lateral (lista de contas à esquerda).
                                    </p>
                                    <div className="modal-actions" style={{ marginTop: '16px' }}>
                                        <button className="btn-cancel" onClick={onClose}>Fechar</button>
                                    </div>
                                </div>
                            ) : (
                            <>
                            {!accountId && accounts.length > 0 && (
                                <div className="input-group-pro" style={{ marginBottom: '12px' }}>
                                    <label>CONTA TELEGRAM</label>
                                    <select
                                        value={String(effectiveAccountId ?? '')}
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            if (accounts.some(a => String(a.id) === id)) {
                                                setSelectedAccountInModal(id);
                                                onSelectAccount?.(id);
                                                setError('');
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '12px 14px',
                                            background: 'rgba(255,255,255,0.06)',
                                            border: '1px solid rgba(255,255,255,0.12)',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            fontSize: '14px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="">Selecione uma conta</option>
                                        {accounts.map((acc) => (
                                            <option key={String(acc.id)} value={String(acc.id)}>
                                                {acc.phone} {acc.status === 'online' ? '• Online' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>Conta que irá “dono” do bot no BotFather.</small>
                                </div>
                            )}
                            <div className="input-group-pro">
                                <label>NOME DE EXIBIÇÃO</label>
                                <div className="input-wrapper">
                                    <User size={16} />
                                    <input placeholder="Ex: Minha Loja" value={name} onChange={handleNameChange} autoFocus />
                                </div>
                            </div>
                            <div className="input-group-pro">
                                <label>USERNAME (@)</label>
                                <div className="input-wrapper">
                                    <Bot size={16} />
                                    <input placeholder="Ex: minha_loja_bot" value={username} onChange={handleUsernameChange} />
                                </div>
                                <small style={{ color: 'var(--text-muted)' }}>Obrigatório terminar com 'bot'</small>
                            </div>

                            {floodWait !== null ? (
                                <div className="flood-wait-box" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center', marginBottom: '15px' }}>
                                    <div style={{ fontSize: '14px', color: '#ef4444', marginBottom: '5px' }}>BotFather pediu para aguardar</div>
                                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#fff' }}>
                                        {Math.floor(floodWait / 60)}:{(floodWait % 60).toString().padStart(2, '0')}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '5px' }}>Tente novamente quando o contador zerar.</div>
                                </div>
                            ) : (
                                error && <div className="error-message">{error}</div>
                            )}

                            {subscriptionRequired && (
                                <div style={{
                                    background: 'rgba(234, 179, 8, 0.12)',
                                    border: '1px solid rgba(234, 179, 8, 0.35)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginTop: '12px',
                                    textAlign: 'center'
                                }}>
                                    <Sparkles size={24} style={{ color: 'rgb(234, 179, 8)', marginBottom: '8px' }} />
                                    <p style={{ color: 'rgb(253, 224, 71)', margin: '0 0 12px', fontSize: '14px' }}>
                                        Para cadastrar novos bots, assine um dos planos da plataforma.
                                    </p>
                                    <Link
                                        to="/plans"
                                        onClick={() => onClose()}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            background: 'rgb(234, 179, 8)',
                                            color: '#000',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        Ver planos
                                    </Link>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={onClose}>Cancelar</button>
                                <GlassButton onClick={handleStep1Create} loading={loading} disabled={floodWait !== null || !effectiveAccountId}>
                                    Próximo <ArrowRight size={16} />
                                </GlassButton>
                            </div>
                            </>
                            )}
                        </>
                    )}

                    {/* STEP 2: CONFIG */}
                    {step === 2 && (
                        <>
                            <div className="step-title">
                                <h4>Configuração (BotFather)</h4>
                                <p>O que as pessoas verão na bio do bot.</p>
                            </div>
                            <div className="input-group-pro">
                                <label>DESCRIÇÃO</label>
                                <div className="input-wrapper">
                                    <MessageSquare size={16} />
                                    <input placeholder="Ex: O melhor bot de vendas..." value={description} onChange={e => setDescription(e.target.value)} />
                                </div>
                            </div>
                            {logs.length > 0 && (
                                <div className="logs-mini" style={{ marginBottom: '16px', color: '#10b981', fontSize: '12px' }}>
                                    ✓ {logs[logs.length - 1]}
                                </div>
                            )}
                            {error && <div className="error-message">{error}</div>}
                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={() => setStep(3)}>Pular</button>
                                <GlassButton onClick={handleStep2Config} loading={loading}>
                                    Salvar e Próximo <ArrowRight size={16} />
                                </GlassButton>
                            </div>
                        </>
                    )}

                    {/* STEP 3: FINAL */}
                    {step === 3 && (
                        <>
                            <div className="step-title">
                                <h4>Configurações Finais</h4>
                                <p>Respostas automáticas do sistema.</p>
                            </div>
                            <div className="input-group-pro">
                                <label>MENSAGEM DE BOAS-VINDAS</label>
                                <textarea
                                    className="input-area"
                                    placeholder="Olá! Bem-vindo ao nosso bot..."
                                    value={welcomeMsg}
                                    onChange={e => setWelcomeMsg(e.target.value)}
                                    rows={3}
                                    style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px', padding: '10px' }}
                                />
                            </div>
                            <div className="input-group-pro">
                                <label>TEXTO DE SUPORTE (/suporte)</label>
                                <textarea
                                    className="input-area"
                                    placeholder="Entre em contato via WhatsApp..."
                                    value={supportText}
                                    onChange={e => setSupportText(e.target.value)}
                                    rows={2}
                                    style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px', padding: '10px' }}
                                />
                            </div>
                            <div className="modal-actions">
                                <GlassButton onClick={handleStep3Final} loading={loading} className="w-full">
                                    <Check size={18} /> Concluir e Salvar
                                </GlassButton>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
